import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-client";
import { defaultSectionTransition } from "../transitions";

import "./screens.scss";
import "./leaderboard.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AppContext, CarColors, ConcertTypes, ProfileContext } from "../contexts/AppContext";
import { AudioContext } from "../contexts/AudioContext";
import { getRemoteScore } from "../services/scoringService";
import { shareOnFacebook, shareOnTwitter } from "../services/socialService";

export default function Leaderboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const keyParam = params.get("key") || "";

  const { setProfile, firstName, lastName, carColor, score, key } = useContext(ProfileContext);
  const { setData, topScores } = useContext(AppContext);

  const [myRank, setMyRank] = useState(-1);

  const { setVisible } = useContext(AudioContext);

  if (!setProfile || !setData) return null;
  if (!topScores?.length || score === -1) {
    getRemoteScore(keyParam)
      .then((result) => {
        if (result.data.score) {
          setProfile({
            firstName: result.data.score.firstName,
            lastName: result.data.score.lastName,
            carColor: result.data.score.carColor,
            concertType: result.data.score.concertType,
            score: result.data.score.score,
            key: result.data.score.key,
          });

          const myIndex = result.data.topScores.findIndex((item) => item.key === result.data.score.key);
          setMyRank(myIndex);
        }
        setData({ topScores: result.data.topScores });
      })
      .catch((error) => {
        console.error("Error fetching score:", error);
      });
  }

  // Check if my score is in the topScores array
  useEffect(() => {
    const myScoreIndex = topScores?.findIndex(
      (item) => item.firstName === firstName && item.lastName === lastName && item.score === score
    );
    if (myScoreIndex !== undefined && myScoreIndex !== -1) {
      setMyRank(myScoreIndex);
    }
  }, [topScores, firstName, lastName, score, setMyRank]);

  interface ScoreProps {
    firstName?: string;
    lastName?: string;
    score?: number;
    carColor?: CarColors;
    concertType?: ConcertTypes;
    rank?: number;
    isFeatured?: boolean;
  }

  const ScoreComponent: React.FC<ScoreProps> = ({ firstName, lastName, score, carColor, rank, isFeatured }) => {
    return (
      <div className={`leaderboard__score ${isFeatured ? "leaderboard__score--featured" : ""}`}  >
        <div className="score__car">
          {rank !== undefined && rank >= 0 && <div className="score__rank">#{rank + 1}</div>}
          <img src={`leaderboard-car-${carColor?.toLowerCase()}.svg`} />
        </div>
        <div className="score__name">
          {firstName} {lastName}
        </div>
        <div className="score__score">
          {score?.toLocaleString()}
          {isFeatured && (
            <div className={`score__featured-tag score__featured-tag--${carColor?.toLowerCase()}`}>My Score</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="screen screen--leaderboard" style={{ backgroundImage: `url(bg-leaderboard.jpg)` }}>
      <AnimatePresence mode={"popLayout"} initial>
        <motion.section
          key="leaderboard--leaderboard"
          className="leaderboard__section leaderboard__section--leaderboard"
          {...defaultSectionTransition}
        >
          <section className="leaderboard__modal">
            <header className="leaderboard__header">
              <h1>Top Scores</h1>
            </header>
            <section className="leaderboard__body">
              <section className="leaderboard__featured-score">
                {typeof score === "number" && score > 0 && myRank === -1 && (
                  <ScoreComponent
                    key="featured-score"
                    firstName={firstName}
                    lastName={lastName}
                    score={score}
                    carColor={carColor}
                    isFeatured={true}
                  />
                )}
              </section>
              <section className="leaderboard__top-scores">
                {!topScores && (
                  <>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <ScoreComponent key={`placeholder-score-${index}`} />
                    ))}
                  </>
                )}
                {topScores &&
                  topScores.map((item, index) => (
                    <ScoreComponent
                      key={`top-score-${index}`}
                      rank={index}
                      firstName={item.firstName}
                      lastName={item.lastName}
                      score={item.score}
                      carColor={item.carColor}
                      concertType={item.concertType}
                      isFeatured={index == myRank}
                    />
                  ))}
              </section>
            </section>

            {key && (
              <>
                <button className="button button--black button--social" onClick={() => shareOnFacebook(key)}>
                  <img src="icon-facebook.svg" />
                </button>
                <button className="button button--black button--social" onClick={() => shareOnTwitter(key)}>
                  <img src="icon-x.svg" />
                </button>
                {/* <button className="button button--black button--social" onClick={() => shareOnLinkedIn(key)}>
                  <img src="icon-linked-in.svg" />
                </button> */}
              </>
            )}

            <button
              className="button button--black"
              onClick={() => {
                setVisible?.(false);
                navigate("/");
              }}
            >
              Play the Game
            </button>
            {key && (<p className="leaderboard__cta">Share results</p>)}
          </section>
        </motion.section>
      </AnimatePresence>
    </section>
  );
}
